// src/pages/Insights/Articles/ArticleDetail.tsx
// Clean, Google-inspired article page optimized for SEO

import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../../../components/SEO/SEO';
import { ArticleSchema, BreadcrumbSchema } from '../../../components/Schema';
import {
  Calendar, Clock, ArrowLeft, Copy,
  ChevronRight, Tag, CheckCircle, BookOpen
} from 'lucide-react';
// import './ArticleDetail.css';
import { OptimizedImage } from '@/components/ui/optimized-image';

// Import all posts
import { allPosts } from './ArticleData';

// Import types
import { Article, ArticleContent } from '../types';

// Import all content modules from the flat content directory (support both .js and .ts)
const contentModules = import.meta.glob('./content/*.{js,ts}', { eager: true }) as Record<string, { default: ArticleContent }>;

const ArticleDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [fullContent, setFullContent] = useState<ArticleContent | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Find current post
  const post = allPosts.find((p: Article) => p.slug === slug);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Load full content
  useEffect(() => {
    if (post && post.contentFile) {
      setLoading(true);
      const modulePathJS = `./content/${post.contentFile}.js`;
      const modulePathTS = `./content/${post.contentFile}.ts`;
      const module = contentModules[modulePathJS] || contentModules[modulePathTS];

      if (module && module.default) {
        setFullContent(module.default);
      } else {
        setFullContent(null);
      }
      setLoading(false);
    }
  }, [post]);

  // Track active heading on scroll

  // Generate heading IDs
  useEffect(() => {
    if (contentRef.current && fullContent) {
      const headings = contentRef.current.querySelectorAll('h2, h3');
      headings.forEach((heading) => {
        if (!heading.id) {
          heading.id = heading.textContent
            ? heading.textContent
              .toLowerCase()
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '-')
            : '';
        }
      });
    }
  }, [fullContent]);

  // Get more articles (random 3 excluding current)
  const moreArticles = allPosts
    .filter((p: Article) => p.id !== post?.id)
    .sort(() => 0.5 - Math.random())
    .slice(0, 3);

  // Helper functions
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getPostUrl = (p: Article) => `/insights/articles/${p.slug}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareTwitter = () => {
    if (!post) return;
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`,
      '_blank'
    );
  };

  const shareLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
      '_blank'
    );
  };

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
      '_blank'
    );
  };

  // 404 handling
  if (!post) {
    return (
      <div className="max-w-full mx-auto p-0 text-[#202124] bg-white font-['Google_Sans',sans-serif]">
        <div className="max-w-[600px] mx-auto py-16 px-6 text-center mt-[100px]">
          <BookOpen size={64} className="text-[#5f6368] mb-6" />
          <h1 className="text-[2rem] font-normal text-[#202124] mb-4">Article Not Found</h1>
          <p className="text-[1rem] text-[#5f6368] mb-8">The article you're looking for doesn't exist or has been moved.</p>
          <Link to="/insights/articles" className="inline-flex items-center gap-2 py-2.5 px-5 bg-[#1a73e8] text-white text-[0.9375rem] font-medium rounded no-underline transition-all duration-200 hover:bg-[#1557b0] hover:-translate-y-0.5">
            <ArrowLeft size={16} />
            Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        key={slug}
        title={`${post.title} | FileFusion`}
        description={post.excerpt}
        keywords={post.keywords ? post.keywords.join(', ') : ''}
        canonical={`https://www.filefusion.app/insights/articles/${post.slug}`}
        image={post.imageUrl}
        publishedDate={post.date}
        author="FileFusion Editorial Team"
        type="article"
      />
      <ArticleSchema
        headline={post.title}
        description={post.excerpt}
        image={post.imageUrl}
        datePublished={post.date}
        author="FileFusion Editorial Team"
        url={`/insights/articles/${post.slug}`}
        keywords={post.keywords || []}
        articleSection="Technology Insights"
        wordCount={post.readTime ? post.readTime * 200 : undefined}
      />
      <BreadcrumbSchema
        breadcrumbs={[
          { name: 'Home', url: 'https://www.filefusion.app/' },
          { name: 'Insights', url: 'https://www.filefusion.app/insights' },
          { name: 'Articles', url: 'https://www.filefusion.app/insights/articles' },
          { name: post.title, url: `https://www.filefusion.app/insights/articles/${post.slug}` }
        ]}
      />

      <div className="max-w-full mx-auto p-0 text-[#202124] bg-white font-['Google_Sans',sans-serif]">

        {/* Article Header */}
        <article className="max-w-[850px] mx-auto py-10 px-5 md:py-16 md:px-6">

          {/* Category & Meta Info */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <Link
              to="/insights/articles"
              className="inline-block text-[0.875rem] font-medium text-[#1a73e8] no-underline py-[6px] px-3.5 bg-[#e8f0fe] rounded-full transition-colors duration-200 hover:bg-[#d2e3fc]"
            >
              Technology Insights
            </Link>
            <div className="flex items-center gap-4 text-[0.875rem] text-[#5f6368]">
              <time dateTime={post.date} className="flex items-center gap-1">
                <Calendar size={14} />
                {formatDate(post.date)}
              </time>
              <span className="flex items-center gap-1">
                <Clock size={14} />
                {post.readTime} min read
              </span>
            </div>
          </div>

          {/* Article Title (H1) */}
          <header className="mb-8">
            <h1 className="text-[1.75rem] md:text-[2.5rem] font-normal leading-[1.3] text-[#202124] mb-4 md:mb-5 tracking-[-0.5px]">{post.title}</h1>
            <p className="text-[1.25rem] leading-[1.6] text-[#5f6368] m-0">{post.excerpt}</p>
          </header>

          {/* Author & Share */}
          <div className="flex flex-wrap gap-4 items-center text-[#5f6368] text-[0.875rem] md:flex-row flex-col md:items-center items-start">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#1a73e8] text-white text-[1.25rem] font-semibold shrink-0">F</div>
              <div className="flex flex-col">
                <span className="text-[0.9375rem] font-medium text-[#202124] leading-[1.4]">FileFusion Editorial Team</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={copyUrl} className="flex items-center justify-center w-9 h-9 rounded-full border border-[#dadce0] bg-white text-[#5f6368] cursor-pointer transition-all duration-200 hover:bg-[#e8f0fe] hover:border-[#1a73e8] hover:text-[#1a73e8]" title="Copy link">
                {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
              </button>
              <button onClick={shareTwitter} className="flex items-center justify-center w-9 h-9 rounded-full border border-[#dadce0] bg-white text-[#5f6368] cursor-pointer transition-all duration-200 hover:bg-[#e8f0fe] hover:border-[#1a73e8] hover:text-[#1a73e8]" title="Share on X">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </button>
              <button onClick={shareLinkedIn} className="flex items-center justify-center w-9 h-9 rounded-full border border-[#dadce0] bg-white text-[#5f6368] cursor-pointer transition-all duration-200 hover:bg-[#e8f0fe] hover:border-[#1a73e8] hover:text-[#1a73e8]" title="Share on LinkedIn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </button>
              <button onClick={shareFacebook} className="flex items-center justify-center w-9 h-9 rounded-full border border-[#dadce0] bg-white text-[#5f6368] cursor-pointer transition-all duration-200 hover:bg-[#e8f0fe] hover:border-[#1a73e8] hover:text-[#1a73e8]" title="Share on Facebook">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Featured Image */}
          {post.imageUrl && (
            <div className="w-full max-w-full mt-10 rounded-lg overflow-hidden aspect-video bg-[#f8f9fa]">
              <OptimizedImage
                src={post.imageUrl}
                alt={post.title}
                width={1200}
                height={675}
                fetchPriority="high"
              />
            </div>
          )}

          {/* Article Content */}
          <div className="mt-10">
            {loading ? (
              <div className="text-center py-16 px-6">
                <div className="w-10 h-10 border-[3px] border-[#dadce0] border-t-[#1a73e8] rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[#5f6368] text-[0.9375rem]">Loading article...</p>
              </div>
            ) : fullContent ? (
              <div
                ref={contentRef}
                className="text-[1.0625rem] leading-[1.8] text-[#202124] [&>h2]:text-[1.75rem] [&>h2]:md:text-[1.75rem] [&>h2]:text-[1.5rem] [&>h2]:font-normal [&>h2]:text-[#202124] [&>h2]:mt-12 [&>h2]:mb-5 [&>h2]:leading-[1.3] [&>h2]:tracking-[-0.5px] [&>h2]:scroll-mt-20 [&>h3]:text-[1.375rem] [&>h3]:md:text-[1.375rem] [&>h3]:text-[1.25rem] [&>h3]:font-normal [&>h3]:text-[#202124] [&>h3]:mt-9 [&>h3]:mb-4 [&>h3]:leading-[1.4] [&>h3]:scroll-mt-20 [&>p]:mb-5 [&>p]:leading-[1.8] [&>ul]:mb-6 [&>ul]:pl-7 [&>ol]:mb-6 [&>ol]:pl-7 [&>blockquote]:my-8 [&>blockquote]:py-5 [&>blockquote]:px-6 [&>blockquote]:border-l-4 [&>blockquote]:border-[#1a73e8] [&>blockquote]:bg-[#f8f9fa] [&>blockquote]:rounded-r [&>blockquote]:italic [&>blockquote]:text-[#5f6368] [&>pre]:bg-[#f8f9fa] [&>pre]:p-5 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:my-6 [&>pre]:border [&>pre]:border-[#dadce0] [&>code]:font-mono [&>code]:text-[0.9em] [&>code]:bg-[#f8f9fa] [&>code]:py-0.5 [&>code]:px-1.5 [&>code]:rounded-[3px] [&>a]:text-[#1a73e8] [&>a]:no-underline [&>a]:border-b [&>a]:border-transparent [&>a:hover]:border-[#1a73e8] [&>img]:max-w-full [&>img]:h-auto [&>img]:rounded-lg [&>img]:my-8 [&_.article-promo]:my-10 [&_.article-promo]:p-8 [&_.article-promo]:bg-[#f8f9fa] [&_.article-promo]:border [&_.article-promo]:border-[#dadce0] [&_.article-promo]:rounded-xl [&_.article-promo>h3]:text-[1.25rem] [&_.article-promo>h3]:font-medium [&_.article-promo>h3]:mt-0 [&_.article-promo>h3]:mb-3 [&_.article-promo>p]:text-[0.9375rem] [&_.article-promo>p]:mb-6 [&_.btn-primary]:inline-flex [&_.btn-primary]:items-center [&_.btn-primary]:gap-2 [&_.btn-primary]:py-3 [&_.btn-primary]:px-6 [&_.btn-primary]:bg-[#1a73e8] [&_.btn-primary]:text-white [&_.btn-primary]:text-[0.9375rem] [&_.btn-primary]:font-medium [&_.btn-primary]:rounded-[4px] [&_.btn-primary]:no-underline [&_.btn-primary]:transition-all [&_.btn-primary]:duration-200 [&_.btn-primary:hover]:bg-[#1557b0] [&_.btn-primary:hover]:shadow-[0_1px_2px_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)]"
                dangerouslySetInnerHTML={{ __html: fullContent.content }}
              />
            ) : (
              <div className="text-center py-16 px-6">
                <p className="text-[#5f6368] text-[0.9375rem]">Article content is being prepared. Check back soon!</p>
              </div>
            )}
          </div>

          {/* Article Footer - Tags & Meta */}
          <footer className="mt-12 pt-8 border-t border-[#dadce0] block">
            {post.keywords && post.keywords.length > 0 && (
              <div className="mb-10">
                <h2 className="flex items-center gap-2 text-[1.125rem] font-medium text-[#202124] mb-4">
                  <Tag size={18} />
                  Related Topics
                </h2>
                <div className="flex flex-wrap gap-2.5">
                  {post.keywords.map((keyword, index) => (
                    <span key={index} className="inline-block py-[6px] px-3.5 bg-[#f8f9fa] text-[#5f6368] text-[0.875rem] font-medium rounded-full border border-[#dadce0] transition-all duration-200 hover:bg-[#e8f0fe] hover:text-[#1a73e8] hover:border-[#1a73e8]">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Author Info Card */}
            <div className="flex gap-5 p-7 bg-[#f8f9fa] rounded-xl border border-[#dadce0] md:flex-row flex-col md:text-left text-center md:items-start items-center">
              <div className="flex items-center justify-center w-[72px] h-[72px] min-w-[72px] rounded-full bg-[#1a73e8] text-white text-[1.75rem] font-semibold shrink-0">F</div>
              <div className="flex flex-col">
                <h3 className="text-[0.875rem] font-semibold text-[#5f6368] mb-2 uppercase tracking-[0.5px]">About the Author</h3>
                <p className="text-[1.125rem] font-medium text-[#202124] mb-3">FileFusion Editorial Team</p>
                <p className="text-[0.9375rem] leading-[1.6] text-[#5f6368] m-0">
                  Our editorial team comprises technology experts and digital productivity
                  specialists dedicated to providing valuable insights on file management,
                  security, and digital innovation.
                </p>
              </div>
            </div>
          </footer>

        </article>

        {/* You May Also Like */}
        {moreArticles.length > 0 && (
          <section className="py-16 bg-white" aria-labelledby="more-heading">
            <div className="max-w-[1200px] mx-auto px-6">
              <h2 id="more-heading" className="text-[1.75rem] font-normal text-[#202124] mb-8 text-center">You May Also Like</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {moreArticles.map((article, index) => (
                  <article key={article.id || index.toString()} className="bg-white border border-[#dadce0] rounded-xl overflow-hidden shadow-[0_1px_2px_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_1px_3px_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)]">
                    <Link to={getPostUrl(article)} className="block no-underline text-inherit">
                      <div className="relative h-[200px] overflow-hidden">
                        <OptimizedImage
                          src={article.imageUrl}
                          alt={article.title}
                          width={400}
                          height={250}
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="text-[1.125rem] font-normal text-[#202124] mb-3 leading-[1.4] line-clamp-2">{article.title}</h3>
                        <p className="text-[0.875rem] text-[#5f6368] mb-4 leading-[1.6] line-clamp-2">{article.excerpt}</p>
                        <div className="flex gap-3 text-[0.8125rem] text-[#5f6368]">
                          <span>{formatDate(article.date)}</span>
                          <span>{article.readTime} min read</span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Newsletter CTA */}
        <div className="max-w-[1240px] mx-auto px-6">
          <section className="py-16 px-6 bg-[#1a73e8] rounded-xl my-12">
            <div className="max-w-[800px] mx-auto text-center">
              <h2 className="text-[2rem] font-normal text-white mb-4">Explore More Insights</h2>
              <p className="text-[1.125rem] text-white/90 mb-8">
                Discover more articles on technology, productivity, security, and digital innovation.
              </p>
              <div className="flex gap-4 justify-center flex-wrap md:flex-nowrap md:items-stretch flex-col md:flex-row">
                <Link to="/insights/articles" className="inline-flex items-center justify-center gap-2 py-3 px-6 bg-white text-[#1a73e8] text-[0.9375rem] font-medium rounded transition-all duration-200 hover:bg-[#f8f9fa] hover:shadow-[0_4px_12px_rgba(0,0,0,0.2)] hover:-translate-y-0.5 whitespace-nowrap">
                  Browse All Articles
                  <ChevronRight size={18} />
                </Link>
                <Link to="/tools" className="inline-flex items-center justify-center gap-2 py-3 px-6 bg-transparent text-white border border-white/60 rounded text-[0.9375rem] font-medium transition-all duration-200 hover:bg-white/10 hover:border-white whitespace-nowrap">
                  Try Our Free Tools
                  <ChevronRight size={18} />
                </Link>
              </div>
            </div>
          </section>
        </div>

      </div>
    </>
  );
};

export default ArticleDetail;
